/**
 * eventosDaChamada — os eventos ao vivo `chamada:*` das ligações do chat (Task 10, 24/10/2026).
 *
 * O servidor (`app/services/chamadas/toque.py`) manda quatro eventos no canal PESSOAL da pessoa,
 * o mesmo transporte do sino: `chamada:chegando` (está tocando para você), `chamada:parou` (parou
 * de tocar: atendida em outro aparelho, recusada, ...), `chamada:atendida` (para quem ligou: alguém
 * atendeu) e `chamada:encerrada` (acabou, com o desfecho). Os campos abaixo são os do servidor,
 * em snake_case, sem tradução.
 *
 * O `NotificationContext.handleEvent` reconhece o evento por `ehEventoDeChamada` e o entrega aqui
 * por `emitirEventoDeChamada`; quem reage (o `CallProvider`, Task 11) assina com
 * `ouvirEventosDeChamada`. O barramento existe porque o `NotificationProvider` e o `CallProvider`
 * são irmãos na árvore: nenhum conhece o outro, e o evento chega ao ouvinte sem passar pelo sino.
 *
 * O `chamada:sala` ("há ligação ativa nesta conversa") NÃO é daqui: viaja no canal da SALA e quem
 * o trata é o `useChat` (invalida a faixa "Entrar" do grupo).
 *
 * A VOLTA DO SINO (`avisarVoltaDoSino`/`ouvirVoltaDoSino`) é o mesmo molde para um aviso sem carga: o
 * transporte do sino voltou depois de uma queda. O pub/sub do Redis não guarda nada, então o
 * `chamada:atendida`/`chamada:encerrada` que passou durante a queda não volta; quem tem uma ligação na
 * tela ouve o aviso e confere o `/estado` no servidor (R2, sobras das ligações).
 */
import type { Desfecho, Midia, PessoaDaLigacao } from "@/types/calls";

/** Está tocando para você. `segundos_restantes` é o que vale: o relógio da máquina pode estar errado. */
export interface ChamadaChegando {
  type: "chamada:chegando";
  call_session_id: string;
  room_id: string;
  /** `DM`, `GROUP` ou `SECTOR_AUTO`. */
  room_type: string;
  /** O rótulo da conversa (o nome do grupo; na conversa direta, o de quem ligou). */
  sala: string;
  quem_ligou: PessoaDaLigacao;
  midia: Midia;
  expira_em: string;
  /** Quantos segundos faltam para o toque acabar (inteiro, nunca negativo). */
  segundos_restantes: number;
}

/** Parou de tocar para você. `motivo`: `atendida` (em outro aparelho seu), `recusada`, ... */
export interface ChamadaParou {
  type: "chamada:parou";
  call_session_id: string;
  motivo: string;
}

/** Para quem ligou: alguém atendeu. */
export interface ChamadaAtendida {
  type: "chamada:atendida";
  call_session_id: string;
  por: PessoaDaLigacao;
}

/** A ligação terminou, com o desfecho gravado no servidor. */
export interface ChamadaEncerrada {
  type: "chamada:encerrada";
  call_session_id: string;
  desfecho: Desfecho;
}

export type EventoDeChamada = ChamadaChegando | ChamadaParou | ChamadaAtendida | ChamadaEncerrada;

const PREFIXO = "chamada:";
/** O evento do canal da sala, que NÃO é do barramento (ver o cabeçalho). */
const EVENTO_DA_SALA = "chamada:sala";

/**
 * É um evento de chamada do canal pessoal? Pelo `type` (prefixo `chamada:`), menos o
 * `chamada:sala`. O prefixo é de propósito: um `chamada:*` novo do servidor chega ao ouvinte (que
 * ignora o que não conhece) em vez de cair nos outros ramos do `handleEvent`.
 */
export function ehEventoDeChamada(data: unknown): data is EventoDeChamada {
  if (typeof data !== "object" || data === null) return false;
  const tipo = (data as { type?: unknown }).type;
  return typeof tipo === "string" && tipo.startsWith(PREFIXO) && tipo !== EVENTO_DA_SALA;
}

type Ouvinte = (evento: EventoDeChamada) => void;

const ouvintes = new Set<{ chamar: Ouvinte }>();

/** Entrega o evento a todos os ouvintes. Um ouvinte que lança não impede os outros nem o transporte. */
export function emitirEventoDeChamada(evento: EventoDeChamada): void {
  // Cópia: um ouvinte pode se cancelar (ou assinar outro) durante a entrega.
  for (const ouvinte of [...ouvintes]) {
    if (!ouvintes.has(ouvinte)) continue;
    try {
      ouvinte.chamar(evento);
    } catch (erro) {
      console.error("[chamadas] ouvinte do evento falhou:", erro);
    }
  }
}

/** Assina os eventos. Devolve a função que cancela (idempotente). */
export function ouvirEventosDeChamada(ouvinte: Ouvinte): () => void {
  // Um objeto por assinatura: o mesmo `ouvinte` assinado duas vezes recebe duas vezes, e cada
  // cancelamento tira só a sua.
  const assinatura = { chamar: ouvinte };
  ouvintes.add(assinatura);
  return () => {
    ouvintes.delete(assinatura);
  };
}

/* ── a volta do sino ──────────────────────────────────────────────────────────────────────────────── */

const ouvintesDaVolta = new Set<{ chamar: () => void }>();

/**
 * O transporte do sino RE-conectou (a primeira conexão da página não conta): o que o servidor mandou durante a
 * queda se perdeu. Um ouvinte que lança não impede os outros nem o transporte.
 */
export function avisarVoltaDoSino(): void {
  for (const ouvinte of [...ouvintesDaVolta]) {
    if (!ouvintesDaVolta.has(ouvinte)) continue;
    try {
      ouvinte.chamar();
    } catch (erro) {
      console.error("[chamadas] ouvinte da volta do sino falhou:", erro);
    }
  }
}

/** Assina a volta do sino. Devolve a função que cancela (idempotente). */
export function ouvirVoltaDoSino(ouvinte: () => void): () => void {
  const assinatura = { chamar: ouvinte };
  ouvintesDaVolta.add(assinatura);
  return () => {
    ouvintesDaVolta.delete(assinatura);
  };
}
